<script setup>
import { hashObject } from '~/composables/hashUtility';
import { buildLocationShortPayload } from '~/utils/location-short-payload';

const props = defineProps({
  leadGenFields: Object,
  heading: String,
  thankYouMessage: String,
  submitButtonText: String,
  locationName: String,
  zeroBounceValidation: {
    type: Boolean,
    default: false
  },
  customLeadGenMessage: String
})
const { validateWithZeroBounce, getLocationShortFormFields, sendLeadGenRequest, sanitizeLeadGenPayload, processLeadSubmission } = useLeadGenData();
const formData = ref(getLocationShortFormFields());
const emailField = ref(null);
const formIsValid = ref(true);
const brochureRequestStore = useBrochureRequest();

const btnSubmitClass = computed(() => {
  return {
    'disabled': !formIsValid.value || requestSent.value
  }
})

const onFieldChanged = (field) => {  
  for (const key in formData.value) {
    // Saving all the fields data while they are being updated    
    if (field.id === key) {      
      formData.value[key].value = field.value;
      formData.value[key].valid = field.valid;
    }    
  } 
}


watch(
  formData,
  (newFormData) => {
    formIsValid.value = newFormData.emailAddress.valid && newFormData.firstName.valid;        
  },
  { deep: true } // Deep watch to track changes in nested properties
);

const requestSent = ref(false)

const buttonText = computed(() => {
  return requestSent.value ? 'Sending...' : props.submitButtonText;
})

const formSubmitted = ref(false)

const handleSubmit = async (event) => {
  const config = useRuntimeConfig()
  requestSent.value = true;
  formData.value.locationName = props.locationName;
  const payload = sanitizeLeadGenPayload(formData.value);
  payload.sourcePageUrl = window?.location?.href;
  const hashedData = hashObject(payload); 

  dataLayer.push({
    'event': 'formSubmitted',
    'leadsUserData': hashedData
  });
  if (props.zeroBounceValidation) {    
    const encodedEmail = encodeURIComponent(payload.emailAddress)        
    const { data, error } = await validateWithZeroBounce(encodedEmail)
   
    if (data.value.status === 'invalid' ) {
      emailField.value.handleState('invalid')
      requestSent.value = false;
      return;
    }
  } 

  // Process the lead submission
  const apiResponse = await processLeadSubmission(payload)
  formSubmitted.value = apiResponse?.status == 200 || false
}

onMounted(() => {
  const form = document.getElementById('short-form') 
  const fieldsets = form.getElementsByTagName('fieldset');
  fieldsets[0].removeAttribute('disabled')
})
</script>

<template>
  <div class="pt-4 pb-5 px-3 " id="location-short-form" data-load="eager">
    <div class="row justify-content-center" v-if="!formSubmitted">
      <div class="col-12 px-5" v-html="heading">

      </div>
      <div class="col-lg-11 col-xs-12">
        <form class="needs-validation " ref="formFields" @submit.prevent="handleSubmit" novalidate id="short-form">
          <fieldset disabled="disabled" class="row justify-content-center align-items-center">
            <div class="col-12 col-lg-4 mb-lg-0 mb-3">
              <FormFloatingLabelInput :id="'firstName'" :placeholderText="leadGenFields.namePlaceholderText" :errorText="leadGenFields.nameErrorMessage" :required="true" @onChanged="onFieldChanged"  />
            </div>
            <div class="col-12 col-lg-4 mb-lg-0 mb-3">
              <FormFloatingLabelInput :id="'emailAddress'" :type="'email'" :placeholderText="leadGenFields.emailFields.emailPlaceholderText" :errorText="leadGenFields.emailFields.emailErrorMessage" :invalidText="leadGenFields.emailFields.invalidEmailErrorMessage" :required="true" @onChanged="onFieldChanged" ref="emailField" />
            </div>
            <div class="col-12 col-lg-3 text-lg-start text-center">
              <button class="btn display btn-id-primary" :class="btnSubmitClass"  type="submit">{{ buttonText }}</button>
            </div>
            <div class="form-disclaimer text-center">
              By signing up you agree to our <a href="#privacy-policy" data-bs-toggle="modal" data-bs-target="#privacy-policy-footer">Privacy Policy</a>
            </div>    
          </fieldset>
        </form>
      </div>
    </div>
    <div class="row justify-content-center" v-else="formSubmitted">
      <div class="col-12 px-5" v-if="brochureRequestStore.isUser && brochureRequestStore.isOptedOutEmail" v-html="customLeadGenMessage"></div>
      <div class="col-12 text-center px-5" v-html="thankYouMessage"></div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
#location-short-form {
  border-radius: 24px;
  background-color: $color-secondary;
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.16);
  border: solid 1px #d9d9d9;
  // When the button is disabled turn into gray and the idea for this form is to get the user attention
  button.disabled {
    opacity: .65;
    color: $color-secondary;
    border-color: $color-id-green;
    background-color: #94D500;
  }  
}
</style>